import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Input } from '~/ui/core/input/Input';

import type { DateFilterBound, DateFilterRange } from '../date-filter';
import {
    DATE_FILTER_INPUT_FORMAT,
    DATE_FILTER_INPUT_PLACEHOLDER,
    formatDateFilterDraft,
    formatDateFilterInput,
    parseCompleteDateFilterInput,
    updateDateFilterRange,
} from '../date-filter';

type Draft = { readonly bound: DateFilterBound; readonly value: string };

const BOUNDS = [
    { bound: 'start', label: 'common.start' },
    { bound: 'end', label: 'common.end' },
] as const;

export const DateRangeInputs: FunctionComponent<{
    readonly field: string;
    readonly range: DateFilterRange;
    readonly onRangeChange: (range: DateFilterRange | undefined) => void;
}> = ({ field, range, onRangeChange }) => {
    const translate = useTranslate();
    const [draft, setDraft] = useState<Draft | null>(null);
    const displayedValue = (bound: DateFilterBound): string =>
        draft?.bound === bound ? draft.value : formatDateFilterInput(range[bound === 'start' ? 0 : 1]);
    const isInvalidDate = (bound: DateFilterBound): boolean =>
        draft?.bound === bound && draft.value.length === DATE_FILTER_INPUT_FORMAT.length;
    const updateDate = (bound: DateFilterBound, typed: string): void => {
        const value = formatDateFilterDraft(displayedValue(bound), typed);
        const nextDate = parseCompleteDateFilterInput(value);
        if (is.nonEmptyString(value) && is.null(nextDate)) {
            setDraft({ bound, value });
            return;
        }

        setDraft(null);
        onRangeChange(updateDateFilterRange(range, bound, nextDate));
    };

    return (
        <div className="flex min-w-[280px] flex-col gap-s p-s">
            {BOUNDS.map(({ bound, label }) => (
                <label
                    key={bound}
                    className="flex flex-col gap-xs text-text-content-subtle text-xxs"
                    htmlFor={`${field}-${bound}`}
                >
                    {translate(label)}
                    <Input
                        autoComplete="off"
                        id={`${field}-${bound}`}
                        inputMode="numeric"
                        placeholder={DATE_FILTER_INPUT_PLACEHOLDER}
                        value={displayedValue(bound)}
                        variant={isInvalidDate(bound) ? 'error' : 'default'}
                        onBlur={() => setDraft(null)}
                        onChange={(event) => updateDate(bound, event.target.value)}
                        onKeyDown={(event) => event.stopPropagation()}
                    />
                </label>
            ))}
        </div>
    );
};
